import { css } from "@emotion/react";
import type { Column } from "@tanstack/react-table";
import type { CSSProperties } from "react";

export const tableCSS = css`
  // fixes table row sizing issues with full height cell children
  // this enables features like hovering anywhere on a cell to display controls
  height: fit-content;
  font-size: var(--global-font-size-s);
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    tr {
      th {
        padding: var(--global-dimension-size-100)
          var(--global-dimension-size-200);
        background-color: var(--global-color-gray-100);
        position: relative;
        text-align: left;
        user-select: none;
        vertical-align: top;
        font-weight: 600;
        font-size: var(--global-font-size-s);
        line-height: var(--global-line-height-s);
        border-bottom: 1px solid var(--global-border-color-default);
        &:not(:last-of-type) {
          border-right: 1px solid var(--global-border-color-default);
        }
        .sort {
          /* The sortable part of the header */
          cursor: pointer;
          display: flex;
          flex-direction: row;
          align-items: center;

          gap: var(--global-dimension-size-50);
        }
        .sort-icon {
          margin-left: var(--global-dimension-size-50);
          font-size: var(--global-font-size-xs);
          vertical-align: middle;
          display: inline-block;
        }
        &:hover .resizer {
          background: var(--global-color-gray-300);
        }
        div.resizer {
          display: inline-block;

          width: 2px;
          height: 100%;
          position: absolute;
          right: 0;
          top: 0;
          cursor: grab;
          z-index: 4;
          touch-action: none;
          &.isResizing,
          &:hover {
            background: var(--global-color-primary);
          }
        }
        // Style action menu buttons in the header
        .button[data-size="compact"][data-childless="true"] {
          padding: 0;
          border: none;
          background-color: transparent;
        }
      }
    }
  }
  tbody:not(.is-empty) {
    tr {
      // when paired with table.height:fit-content, allows table cells and their children to fill entire row height
      height: 100%;
      &:not(:last-of-type) {
        & > td {
          border-bottom: 1px solid var(--global-color-gray-200);
        }
      }
      & > td {
        padding: var(--global-dimension-size-100)
          var(--global-dimension-size-200);
      }
      &[data-selected="true"] {
        background-color: var(--global-color-primary-100);
      }
    }
  }
`;

export const borderedTableCSS = css`
  tbody:not(.is-empty) {
    tr {
      & > td {
        border-bottom: 1px solid var(--global-color-gray-100);
      }
      & > td:not(:last-of-type) {
        border-right: 1px solid var(--global-color-gray-100);
      }
    }
  }
`;

export const interactiveTableCSS = css`
  tbody:not(.is-empty) {
    tr {
      &:hover {
        background-color: var(--hover-background);
      }
    }
  }
`;

export const selectableTableCSS = css(
  tableCSS,
  interactiveTableCSS,
  css`
    tbody:not(.is-empty) {
      tr {
        cursor: pointer;
      }
    }
  `
);

export const paginationCSS = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: var(--global-dimension-size-100);
  gap: var(--global-dimension-size-50);
  border-top: 1px solid var(--global-color-gray-300);
`;

//These are the important styles to make sticky column pinning work!
//Apply styles like this using your CSS strategy of choice with this kind of logic to head cells, data cells, footer cells, etc.
//View the index.css file for more needed styles such as border-collapse: separate
export function getCommonPinningStyles<Row>(
  column: Column<Row>
): CSSProperties {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    filter: isLastLeftPinnedColumn
      ? "drop-shadow(4px 0 4px rgba(0, 0, 0, 0.1))"
      : isFirstRightPinnedColumn
        ? "drop-shadow(-4px 0 4px rgba(0, 0, 0, 0.1))"
        : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    backgroundColor: isPinned ? "var(--global-color-gray-100)" : undefined,
  };
}
